/**
 * Shared helpers for Zod-validated forms — every app's form layer.
 *
 * Form validation runs the SAME shared request schemas the backend
 * enforces (this package) — one source of truth, so the inline field
 * error a user sees matches the 400 the wire would return. Screens with
 * form-only rules (confirmPassword matching, password-strength UX) extend
 * the shared schema locally rather than re-declaring fields.
 *
 * Lived in apps/web/src/utils until the mobile auth screens needed the
 * identical treatment (2026-09-02). Mobile had kept a hand-rolled email
 * regex that capped the TLD at three letters, so a `.studio` address was
 * refused on the phone while web and the backend accepted it. The helper
 * lives here, next to the schemas it runs, so no app can re-grow a
 * private copy of the rule.
 */
import type { z } from 'zod';

/**
 * Run `schema.safeParse(values)` and return a field→message map (first
 * message per field, only fields in the caller's key union). Returns
 * `null` when validation passes — callers gate submission on that:
 *
 *   const errors = zodToFieldErrors(SigninRequestSchema, values, ['email', 'password']);
 *   if (errors) { setErrors(errors); return; }
 */
export function zodToFieldErrors<K extends string>(
  schema: z.ZodType,
  values: unknown,
  fields: readonly K[]
): Partial<Record<K, string>> | null {
  const result = schema.safeParse(values);
  if (result.success) return null;

  const fieldErrors: Partial<Record<K, string>> = {};
  for (const issue of result.error.issues) {
    const path = issue.path[0] as K | undefined;
    if (path && fields.includes(path) && !fieldErrors[path]) {
      fieldErrors[path] = issue.message;
    }
  }
  return fieldErrors;
}

/**
 * The request field a refusal is about, when the backend named one: a 400
 * (or 409) for a rule the request schema cannot check on its own carries it
 * beside the message, as `data.field` in the response body (the backend's
 * HttpError). A screen matches this, never the message's words: the listing
 * page shows the message on that field, and a refusal on
 * VENDOR_TERMS_REFUSAL_FIELD opens the vendor terms, on web and in the app.
 *
 * `err` is a rejected request as either app's RTK Query base query shapes
 * it (`{ status, data }`, `data` being the response body). Null when the
 * rejection names no field, or is not such a rejection at all.
 *
 * Lived in web's axiosBaseQuery until the app needed the identical read
 * (2026-10-01); here no app grows its own copy.
 */
export function refusalFieldOf(err: unknown): string | null {
  const body = refusalBodyOf(err);
  if (body === null) return null;
  const payload = (body as { data?: unknown }).data;
  if (typeof payload !== 'object' || payload === null) return null;
  const field = (payload as { field?: unknown }).field;
  return typeof field === 'string' ? field : null;
}

/**
 * The backend's own words for a refusal (`message` in the response body),
 * for a screen to show as they are. Null when the rejection carries none: a
 * network failure, a thrown Error, a response with no body. The caller says
 * what to show then; this never invents a message.
 */
export function refusalMessageOf(err: unknown): string | null {
  const body = refusalBodyOf(err);
  if (body === null) return null;
  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' && message !== '' ? message : null;
}

/** The response body of a rejected request (`{ status, data }`), when it is an object. */
function refusalBodyOf(err: unknown): object | null {
  if (typeof err !== 'object' || err === null) return null;
  const body = (err as { data?: unknown }).data;
  return typeof body === 'object' && body !== null ? body : null;
}
